// import { takeLatest, takeEvery, select, call, put, all } from 'redux-saga/effects'
import { takeLatest, select, call, put, all } from 'redux-saga/effects'
import _, { keys, keyBy, map, groupBy, mapValues, chunk, filter, isEqual, cloneDeep, omit, get } from 'lodash'
import axios from 'axios'
import cuid from 'cuid'
import { delay } from 'redux-saga'
import moment from 'moment'

import config from 'config/index'
import { createAlert } from 'actions/app-actions'
import constants, {
  LIMIT_EXTENDED_SEARCH_RESULTS,
  aggregationSettings,
  searchToolTabs
} from '../constants/niq-constants'

import {
  NIQ_QUERY_RUN_STARTED,
  NIQ_QUERY_RUN_SUCCEEDED,
  NIQ_QUERY_RUN_FAILED,
  // QUERY_SET_QUALITY,
  NIQ_AUTOSUGGEST_SUCCEEDED,
  NIQ_AUTOSUGGEST_FAILED,
  NIQ_SEARCH_TERM_SUGGEST,
  NIQ_FETCH_TYPE_CHILDREN,
  NIQ_FETCH_TYPE_CHILDREN_SUCCEEDED,
  NIQ_FETCH_TYPE_CHILDREN_FAILED,
  NIQ_TOGGLE_QC_MODE,
  NIQ_GET_SEARCH_METADATA,
  NIQ_GET_SEARCH_METADATA_SUCCEEDED,
  NIQ_GET_SEARCH_METADATA_FAILED,
  NIQ_CHANGE_CATEGORY_FILTER,
  NIQ_APPLY_QUERY,
  NIQ_FETCH_SAVED_GROUP,
  NIQ_FETCH_SAVED_GROUP_SUCCEEDED,
  NIQ_FETCH_SAVED_GROUP_FAILED,
  NIQ_GROUP_CREATE,
  NIQ_CREATE_GROUP_SUCCEEDED,
  NIQ_CREATE_GROUP_FAILED,
  NIQ_FETCH_QUERIES_BY_GROUP,
  NIQ_FETCH_QUERIES_BY_GROUP_SUCCEEDED,
  NIQ_FETCH_QUERIES_BY_GROUP_FAILED,
  NIQ_QUERY_CREATE,
  NIQ_CREATE_QUERY_SUCCEEDED,
  NIQ_CREATE_QUERY_FAILED,
  NIQ_DELETE_GROUP,
  NIQ_DELETE_GROUP_SUCCEEDED,
  NIQ_DELETE_GROUP_FAILED,
  NIQ_DELETE_QUERY,
  NIQ_DELETE_QUERY_SUCCEEDED,
  NIQ_DELETE_QUERY_FAILED,
  NIQ_SAVE_SEARCH_QUERY,
  NIQ_SAVE_SEARCH_QUERY_SUCCEEDED,
  NIQ_SAVE_SEARCH_QUERY_FAILED,
  NIQ_QUERY_STATS_STARTED,
  NIQ_QUERY_STATS_SUCCEEDED,
  NIQ_QUERY_STATS_FAILED,
  NIQ_RESET_QUERY_HEADER,
  NIQ_SET_ACTIVE_SEARCH_TAB,
  NIQ_GET_QUERY_SQL,
  NIQ_GET_QUERY_SQL_STARTED,
  NIQ_GET_QUERY_SQL_SUCCEEDED,
  NIQ_GET_QUERY_SQL_FAILED,
  NIQ_AUTOSUGGEST_LOADING_START
} from '../actions/niq-search-actions'

import {
  NIQ_SEARCH_TOGGLE_LOCK_DATA,
  NIQ_DESCRIPTION_SAVE_EDITS,
  NIQ_DESCRIPTION_SAVE_EDITS_STARTED,
  NIQ_DESCRIPTION_SAVE_EDITS_SUCCEEDED,
  NIQ_DESCRIPTION_SAVE_EDITS_FAILED,
  NIQ_DESCRIPTION_BRAND_SUGGEST,
  NIQ_DESCRIPTION_BRAND_SUGGEST_SUCCEEDED,
  NIQ_DESCRIPTION_CATEGORY_SUGGEST,
  NIQ_DESCRIPTION_CATEGORY_SUGGEST_SUCCEEDED,
  NIQ_DESCRIPTION_EDIT_STATUS_UPDATED,
  NIQ_REFRESH_DESCRIPTION_EDIT_STATUS,
  NIQ_DESCRIPTION_EDIT_STATUS_UPDATE_FAILED,
  NIQ_TIME_DIMENSION_CHANGED,
  NIQ_TIME_AGGREGATION_CHANGED,
  NIQ_UPDATE_AGGREGATION,
  NIQ_SORT_TABLE,
  NIQ_SEARCH_FILTER_CHANGED,
  NIQ_SEARCH_REFRESH_DESCRIPTION_DATA,
  NIQ_APPLY_TEMP_FILTER,
  NIQ_REMOVE_TEMP_FILTER,
  NIQ_UPDATE_WIDGET_DATA,
  NIQ_UPDATE_STATS_DATA,
  NIQ_FETCH_DESCRIPTION_EDIT_SUGGESTIONS,
  NIQ_FETCH_DESCRIPTION_EDIT_SUGGESTIONS_SUCCEEDED,
  NIQ_FETCH_DESCRIPTION_EDIT_SUGGESTIONS_FAILED
} from '../actions/niq-widget-actions'

import createESQuery from '../utils/niq-es-query-utils'
import { buildAggregationRequest } from '../utils/niq-agg-request-helper'

import { NIQ_SEARCH_STORE_PATH, NIQ_SEARCH_EDIT_STORE_PATH } from '../reducers/niq-reducers'
import { CODING_MODE, ITEM_TYPE, VALIDATION_FLAG } from '../config'
import { ES_SCORECARD, ES_SCORECARD_KEY } from 'constants/constants'

// This is the whitelist of actions for which a search query will run. For any of these actions, shouldQuery is executed
const queryActionWhitelist = [
  NIQ_APPLY_QUERY,
  NIQ_SEARCH_FILTER_CHANGED,
  NIQ_UPDATE_AGGREGATION,
  NIQ_SEARCH_TOGGLE_LOCK_DATA,
  NIQ_TOGGLE_QC_MODE,
  NIQ_CHANGE_CATEGORY_FILTER,
  NIQ_SET_ACTIVE_SEARCH_TAB
]

const overTimeWidgetActionList = [
  NIQ_TIME_DIMENSION_CHANGED,
  NIQ_TIME_AGGREGATION_CHANGED,
  NIQ_APPLY_QUERY,
  NIQ_SET_ACTIVE_SEARCH_TAB,
  NIQ_SEARCH_FILTER_CHANGED
]

const {
  aggregations: {
    BRAND,
    DESCRIPTION,
    DESCRIPTION_FIRST_WORD,
    CATEGORY,
    MERCHANT,
    TIME,
    CONTEXT_REVENUE,
    CONTEXT_OTHER_REVENUE,
    QUERIED_REVENUE,
    QUERIED_OTHER_REVENUE,
    CONTEXT_DESCRIPTION_COUNT,
    QUERIED_DESCRIPTION_COUNT,
    STATS,
    SLICE_CATEGORY,
    SLICE_BRAND,
    INVALID_CATEGORY,
    INVALID_BRAND,
    OMNISALES_MODULE
  }
} = constants

const CURRENT_DEFAULT_AGGREGATIONS = [
  DESCRIPTION,
  BRAND,
  MERCHANT,
  CATEGORY,
  DESCRIPTION_FIRST_WORD,
  SLICE_CATEGORY,
  SLICE_BRAND,
  INVALID_CATEGORY,
  INVALID_BRAND,
  OMNISALES_MODULE
]

const { EDIT_MODE, TRENDS } = searchToolTabs

const getSearch = state => state[NIQ_SEARCH_STORE_PATH]
const getSearchEdit = state => state[NIQ_SEARCH_EDIT_STORE_PATH]

// Map data for server call
function* transformTreeState({
  aggType,
  queryFilters,
  ignoreQuery = false,
  // monitoring = {},
  useFullDateRange = false
}) {
  const search = yield select(getSearch)
  const searchEdit = yield select(getSearchEdit)

  const query = ignoreQuery ? null : createESQuery(search.queryTree, true)
  let terms = [{ fieldName: 'in_current', values: [true] }]

  // for category filter
  if (search.searchFilters?.categoryFilterList?.length) {
    terms = [...terms, createCategoryFilter(search.searchFilters.categoryFilterList)]
  }

  // for omnisales_module filter
  if (search.searchFilters?.omnisalesFilterList?.length) {
    terms = [...terms, createOmnisalesFilter(search.searchFilters.omnisalesFilterList)]
  }

  // for user filter
  if (search.searchFilters.selectedBrandUsers.length) {
    terms = [...terms, ...prepareUserFilter('brand_added_by', search.searchFilters.selectedBrandUsers)]
  }

  if (search.searchFilters.selectedCategoryUsers.length) {
    terms = [...terms, ...prepareUserFilter('category_added_by', search.searchFilters.selectedCategoryUsers)]
  }

  if (queryFilters && queryFilters.terms) {
    terms = [...terms, ...queryFilters.terms]
  }

  if (aggregationSettings[aggType]?.term) {
    terms = [...terms, aggregationSettings[aggType]?.term]
  }

  // converting selected dates to their utc timezone equivalent
  let fromDate = search.searchFilters.from
  let toDate = search.searchFilters.to
  if (useFullDateRange) {
    fromDate = search.searchFilters.min_range
    toDate = search.searchFilters.max_range
  }
  const fromTimestamp = moment.utc(fromDate).valueOf()
  const toTimestamp = moment.utc(toDate).valueOf()

  return {
    query,
    aggregationFields: { [aggType]: buildAggregationRequest(aggType, searchEdit[aggType], fromTimestamp, toTimestamp) },
    filters: {
      ...omit(search.searchFilters, ['selectedBrandUsers', 'selectedCategoryUsers']),
      from: fromTimestamp,
      to: toTimestamp,
      terms
    },
    queryCurrent: true,
    esTimeout: config.extendedTimeout
  }
}
function createCategoryFilter(categoryFilterList) {
  return {
    fieldName: constants.esFields.category,
    values: categoryFilterList.map(filter => filter.value)
  }
}
function createOmnisalesFilter(list) {
  return {
    fieldName: constants.esFields.omnisales_module,
    values: list.map(filter => filter.value)
  }
}

function prepareUserFilter(type, selectedUsers) {
  const clonedUserList = [...selectedUsers]
  if (clonedUserList.includes('all users')) {
    clonedUserList.splice(clonedUserList.indexOf('all users'), 1)
  }
  return [
    {
      fieldName: type,
      values: clonedUserList
    }
  ]
}

function* postSuccessForAggQuery(aggType, data) {
  if (aggType === DESCRIPTION) {
    const ids = map(data[DESCRIPTION], 'dictionary_key')
    yield fetchDescriptionEditStatus(ids)
  }
}

function* fetchAllDescriptionEditStatus() {
  const searchEdit = yield select(getSearchEdit)
  yield fetchDescriptionEditStatus(map(searchEdit.description.data, 'dictionary_key'))
}

function* fetchDescriptionEditStatus(ids) {
  try {
    if (!ids || !ids.length) {
      return
    }
    const batches = chunk(ids, 500)
    for (const batch of batches) {
      const result = yield call(() =>
        axios.post('/api/niq/dictionary/entries/search', {
          ids: batch
        })
      )
      if (result.status === 200) {
        yield put({
          type: NIQ_DESCRIPTION_EDIT_STATUS_UPDATED,
          data: processDictUpdateData(result.data)
        })
      } else {
        yield put({
          type: NIQ_DESCRIPTION_EDIT_STATUS_UPDATE_FAILED,
          alerts: [
            {
              id: cuid(),
              type: 'warning',
              message: 'There was an error while fetching edit status for descriptions',
              headline: 'Edit Items'
            }
          ],
          message: 'Backend Error'
        })
      }
    }
  } catch (error) {
    window.captureException(error)
    yield put({
      type: NIQ_DESCRIPTION_EDIT_STATUS_UPDATE_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'warning',
          message: 'There was an error while fetching edit status for descriptions',
          headline: 'Edit Items'
        }
      ],
      message: error.message
    })
  }
}

function processDictUpdateData(data) {
  const groupedById = groupBy(data, 'id')
  return mapValues(groupedById, arr => {
    return keyBy(arr, 'attributeEdited')
  })
}

function* query(aggType, queryFilters, isTemp) {
  const search = yield select(getSearch)
  if (search.isLocked) {
    // if data reloading is locked don't run any query
    return
  }
  const apiPath = '/api/es/query'
  try {
    yield put({
      type: NIQ_QUERY_RUN_STARTED,
      aggType,
      loading: true,
      isTemp
    })
    const postParams = yield transformTreeState({ aggType, queryFilters })
    const queryResult = yield call(() => axios.post(apiPath, postParams, { timeout: config.extendedTimeout }))
    yield put({
      type: NIQ_QUERY_RUN_SUCCEEDED,
      data: queryResult.data,
      aggType,
      loading: false,
      isTemp
    })
    yield postSuccessForAggQuery(aggType, queryResult.data)
  } catch (error) {
    window.captureException(error)
    yield put({
      type: NIQ_QUERY_RUN_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'danger',
          message: error.message,
          headline: `${aggType}`
        }
      ],
      alertTimeout: 10000,
      message: error.message,
      aggType,
      loading: false
    })
  }
}

function* queryStats(aggType, queryFilters, ignoreQuery, statType) {
  const search = yield select(getSearch)
  if (search.isLocked) {
    // if data reloading is locked don't run any query
    return
  }
  const apiPath = '/api/es/query'
  const queryParams = { aggType, statType }
  try {
    yield put({
      type: NIQ_QUERY_STATS_STARTED,
      ...queryParams,
      loading: true
    })
    const postParams = yield transformTreeState({ aggType, queryFilters, ignoreQuery })
    const queryResult = yield call(() => axios.post(apiPath, postParams, { timeout: config.extendedTimeout }))
    yield put({
      type: NIQ_QUERY_STATS_SUCCEEDED,
      data: queryResult.data,
      ...queryParams,
      loading: false
    })
  } catch (error) {
    window.captureException(error)
    yield put({
      type: NIQ_QUERY_STATS_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'danger',
          message: error.message,
          headline: `Stats query: ${aggType}`
        }
      ],
      alertTimeout: 10000,
      message: error.message,
      ...queryParams,
      loading: false
    })
  }
}

function validateSearchFilters({ brandFilters, categoryFilters }) {
  return filter(brandFilters, value => value).length === 0 || filter(categoryFilters, value => value).length === 0
}

/** TODO: move following to state */
let previousQueryTree = null

function* shouldQuery(action) {
  const search = yield select(getSearch)
  if (action.type !== 'SEARCH_FILTER_CHANGED' || action.key === ES_SCORECARD_KEY) {
    yield put({
      type: NIQ_GET_SEARCH_METADATA
    })
  }

  if (search.isActiveTab !== EDIT_MODE || search.isLocked) {
    return
  }
  const queryTree = createESQuery(search.queryTree, true)
  if (search.isStale[EDIT_MODE] || !isEqual(previousQueryTree, queryTree) || (action.data && action.data.updateAggs)) {
    if (validateSearchFilters(search.searchFilters)) {
      yield put({
        type: NIQ_QUERY_RUN_FAILED,
        alerts: [
          {
            id: cuid(),
            type: 'warning',
            message: `User must select at least one Brand and Category filter`,
            headline: `No filters applied`
          }
        ],
        alertTimeout: 6000
      })
      return
    }
    const currentAggregations =
      action.data && action.data.updateAggs ? action.data.updateAggs : CURRENT_DEFAULT_AGGREGATIONS
    // TODO: fix this
    yield queryAll(currentAggregations)
    previousQueryTree = cloneDeep(queryTree)
    if (search.widgetEnabledStatus[STATS]) {
      yield all([contextDataStats(), queryDataStats()])
    }
  }
}

function* queryAll(currentAggregations = CURRENT_DEFAULT_AGGREGATIONS, filters) {
  const search = yield select(getSearch)
  yield currentAggregations
    .filter(aggregationType => {
      if (aggregationType in search.widgetEnabledStatus) {
        return search.widgetEnabledStatus[aggregationType]
      }
      return true
    })
    .map(aggregationType => {
      return query(aggregationType, filters)
    })
}

function* queryDataStats(queryParams = {}) {
  const otherBrandParams = cloneDeep(queryParams)
  if (!otherBrandParams.terms) {
    otherBrandParams.terms = []
  }
  otherBrandParams.terms.push({
    fieldName: constants.esFields.brand,
    values: ['other']
  })

  yield all([
    queryStats(QUERIED_REVENUE, { ...queryParams }, false),
    queryStats(QUERIED_DESCRIPTION_COUNT, { ...queryParams }, false, 'descriptionStat'),
    queryStats(QUERIED_OTHER_REVENUE, otherBrandParams, false)
  ])
}

function* contextDataStats() {
  const otherBrandParams = {
    terms: [
      {
        fieldName: constants.esFields.brand,
        values: ['other']
      }
    ]
  }
  yield all([
    queryStats(CONTEXT_REVENUE, null, true),
    queryStats(CONTEXT_DESCRIPTION_COUNT, null, true, 'descriptionStat'),
    queryStats(CONTEXT_OTHER_REVENUE, otherBrandParams, true)
  ])
}

function* applyTempFilter(action) {
  const search = yield select(getSearch)
  const aggsToQuery = filter(CURRENT_DEFAULT_AGGREGATIONS, agg => {
    return agg !== action.aggType && search.widgetEnabledStatus[agg]
  })
  return yield queryWithTempFilter(aggsToQuery)
}

function* getTempFilterParam() {
  const searchEdit = yield select(getSearchEdit)
  const tempFilters = searchEdit.description.tempFilters
  const tempFilterParams = keys(tempFilters).map(aggType => {
    return {
      fieldName: constants.esFields[aggType],
      values: [tempFilters[aggType]]
    }
  })
  return {
    terms: tempFilterParams
  }
}

function* queryWithTempFilter(aggsToQuery = CURRENT_DEFAULT_AGGREGATIONS) {
  const search = yield select(getSearch)
  const queryParams = yield getTempFilterParam()
  yield aggsToQuery.map(aggregationType => {
    return query(aggregationType, { ...queryParams }, true)
  })
  if (search.widgetEnabledStatus[STATS]) {
    yield queryDataStats({ ...queryParams })
  }
}

function* removeTempFilter(action) {
  const search = yield select(getSearch)
  const aggsToQuery = filter(CURRENT_DEFAULT_AGGREGATIONS, agg => {
    return search.widgetEnabledStatus[agg]
  })
  return yield queryWithTempFilter(aggsToQuery)
}

function* queryDescriptions() {
  const search = yield select(getSearch)
  if (search.widgetEnabledStatus[DESCRIPTION]) {
    yield queryWithTempFilter([DESCRIPTION])
  }
}

function* autosuggest(action) {
  yield call(delay, 300) // debounce
  const search = yield select(getSearch)
  const filterApiMap = {
    brand: {
      path: '/api/niq/taxonomy/brands/suggestions',
      params: {
        responseType: 'qc-tool',
        fullPath: search.searchValue,
        isLeaf: action.excludeParents,
        isActive: action.excludeInactive,
        limit: 100
      }
    },
    category: {
      path: '/api/niq/taxonomy/categories/suggestions',
      params: {
        responseType: 'qc-tool',
        fullPath: search.searchValue,
        isLeaf: action.excludeParents,
        isActive: action.excludeInactive,
        limit: 100
      }
    },
    omnisales_module: {
      path: '/api/niq/taxonomy/omnisales/suggestions',
      params: {
        fullPath: search.searchValue,
        limit: 50
      }
    },
    slice_category: {
      path: '/api/taxonomy/categories/suggestions',
      params: {
        responseType: 'qc-tool',
        fullPath: search.searchValue,
        isLeaf: action.excludeParents,
        isActive: action.excludeInactive,
        limit: 100
      }
    },
    slice_brand: {
      path: '/api/taxonomy/brands/suggestions',
      params: {
        responseType: 'qc-tool',
        fullPath: search.searchValue,
        isLeaf: action.excludeParents,
        isActive: action.excludeInactive,
        limit: 100
      }
    },
    merchant: {
      path: '/api/niq/es/autocomplete',
      params: {
        fieldName: 'merchant_name',
        searchText: search.searchValue,
        limit: 50
      }
    },
    [CODING_MODE]: {
      path: '/api/niq/es/autocomplete',
      params: {
        fieldName: CODING_MODE,
        searchText: search.searchValue || '',
        limit: 100
      }
    },
    [ITEM_TYPE]: {
      path: '/api/niq/es/autocomplete',
      params: {
        fieldName: ITEM_TYPE,
        searchText: search.searchValue || '',
        limit: 100
      }
    },
    brand_owner: {
      path: '/api/niq/es/autocomplete',
      params: {
        fieldName: 'brand_owner',
        searchText: search.searchValue,
        limit: 50
      }
    },
    [VALIDATION_FLAG]: {
      path: '/api/niq/es/autocomplete',
      params: {
        fieldName: VALIDATION_FLAG,
        searchText: search.searchValue,
        limit: 50
      }
    }
  }

  if (!filterApiMap[action.filter]) {
    return
  }
  try {
    yield put({
      type: NIQ_AUTOSUGGEST_LOADING_START
    })
    const { path, params } = filterApiMap[action.filter]
    const autoSuggestResult = yield call(() => axios.get(path, { params }))

    let data = autoSuggestResult.data.suggestions
    if (_.isEmpty(data) || (data.length === 1 && _.isEmpty(data[0]))) {
      data = []
    }

    yield put({
      type: NIQ_AUTOSUGGEST_SUCCEEDED,
      data: [...data]
    })
  } catch (error) {
    window.captureException(error)
    yield put({
      type: NIQ_AUTOSUGGEST_FAILED,
      data: error.message
    })
    yield put(createAlert('danger', error.message, `Failed to get auto suggestions`))
  }
}

function* autosuggestEdits(action) {
  yield call(delay, 300) // debounce
  /* @todo TODO: Data from server is correct but the client side does not show the right auto complete values that contain > . The below replace is to partially fix this problem */
  const searchValue = (action.data.text || '').replace(/\s*>\s*/g, ' > ')
  const searchType = action.data.type
  const categoryFullPath = action.data.categoryFullPath
  const filterApiMap = {
    brand_name: '/api/niq/taxonomy/brands/suggestions',
    category_full_path: '/api/niq/taxonomy/categories/suggestions',
    omnisales_module: '/api/niq/taxonomy/omnisales/suggestions'
  }
  const params = {
    responseType: 'qc-tool',
    fullPath: searchValue,
    isLeaf: true,
    isActive: true,
    limit: 100,
    categoryFullPath
  }
  try {
    const autoSuggestResult = yield call(() => axios.get(filterApiMap[searchType], { params }))

    yield put({
      type:
        searchType === 'brand_name'
          ? NIQ_DESCRIPTION_BRAND_SUGGEST_SUCCEEDED
          : NIQ_DESCRIPTION_CATEGORY_SUGGEST_SUCCEEDED,
      data: autoSuggestResult.data.suggestions
    })
  } catch (error) {
    window.captureException(error)
    // TODO: FIX MISSING ACTIONS
    // yield put({ type: searchType === 'brand_name' ? DESCRIPTION_BRAND_SUGGEST_FAILED : DESCRIPTION_CATEGORY_SUGGEST_FAILED,
    //   alerts: [{
    //     id: cuid(),
    //     type: 'danger',
    //     message: `Failed to get suggestions for ${searchType === 'brand_name' ? 'brand' : 'category'}`,
    //     headline: 'Search Suggestions'
    //   }],
    //   alertTimeout: 10000,
    //   data: error.message
    // })
  }
}

function* saveDictionaryEntries(action) {
  const searchEdit = yield select(getSearchEdit)
  try {
    yield put({
      type: NIQ_DESCRIPTION_SAVE_EDITS_STARTED,
      alerts: [
        {
          id: cuid(),
          type: 'info',
          message: 'Submitting changes...',
          headline: 'Dictionary Edits'
        }
      ]
    })
    const removeKeys = yield batchSaveDictionary(searchEdit.description.stagedEdits, searchEdit.description.data)
    yield put({
      type: NIQ_DESCRIPTION_SAVE_EDITS_SUCCEEDED,
      removeKeys,
      alerts: [
        {
          id: cuid(),
          type: 'success',
          message: 'All changes were successfully submitted.',
          headline: 'Dictionary Edits'
        }
      ]
    })
  } catch (error) {
    window.captureException(error)
    // TODO: handle timeouts and other errors
    yield put({
      type: NIQ_DESCRIPTION_SAVE_EDITS_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'danger',
          message: 'An error occurred while creating edit jobs.',
          headline: 'Dictionary Edit'
        }
      ],
      alertTimeout: 10000,
      message: error.message
    })
  } finally {
    // Refresh description edits after dictionary update
    const ids = map(searchEdit.description.data, 'dictionary_key')
    yield fetchDescriptionEditStatus(ids)
  }
}

function* batchSaveDictionary(stagedEdits, descriptions) {
  const search = yield select(getSearch)
  const selectedIndex = get(search, 'searchFilters.' + ES_SCORECARD_KEY)

  const edits = []
  const updateKeys = []

  _(descriptions)
    .keyBy('dictionary_key')
    .at(keys(stagedEdits))
    .value()
    .forEach(entry => {
      if (entry && entry.dictionary_key) {
        edits.push({
          ...entry,
          selectedIndex,
          edits: stagedEdits[entry.dictionary_key]
        })
        updateKeys.push(entry.dictionary_key)
      }
    })

  const batches = chunk(edits, 100)
  yield batches.map(batch =>
    call(() =>
      axios.post('/api/niq/dictionary/update', {
        rows: batch
      })
    )
  )
  return updateKeys
}

function* getSearchMetaData() {
  try {
    const search = yield select(getSearch)
    const index = ES_SCORECARD.NIQ
    const result = yield call(() => axios.get('/api/niq-search-tool/metadata', { params: { index } }))
    if (result && result.data) {
      const { dataRefresh, dateRange, descUpdateUsers } = result.data
      const dateRangeInit = search.searchFilters.dateRangeInit
      const data = {
        dataRefresh,
        dateRange: {
          from: dateRangeInit
            ? moment.utc(dateRange[0]).format(constants.dateFormat.qcTool)
            : search.searchFilters.from,
          to: dateRangeInit ? moment.utc(dateRange[1]).format(constants.dateFormat.qcTool) : search.searchFilters.to,
          min_range: moment.utc(dateRange[0]).format(constants.dateFormat.qcTool),
          max_range: moment.utc(dateRange[1]).format(constants.dateFormat.qcTool)
        },
        dateRangeInit: false,
        brandUpdateUsers: descUpdateUsers.brand_added_by,
        categoryUpdateUsers: descUpdateUsers.category_added_by
      }
      yield put({
        type: NIQ_GET_SEARCH_METADATA_SUCCEEDED,
        data
      })
    }
  } catch (error) {
    window.captureException(error)
    yield put({
      type: NIQ_GET_SEARCH_METADATA_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'danger',
          message: `There was an error loading metadata for Search / QC tool`,
          headline: 'Search / QC tool'
        }
      ],
      alertTimeout: 10000
    })
  }
}

function* fetchTypeChildren(action) {
  try {
    const replaceRegex = new RegExp(`^${constants.parentElementPrefix}`)
    const searchValue = action.data.value.replace(replaceRegex, '')
    const filterApiMap = {
      brand: '/api/niq/taxonomy/brands/suggestions',
      category: '/api/niq/taxonomy/categories/suggestions',
      slice_category: '/api/taxonomy/categories/suggestions',
      slice_brand: '/api/taxonomy/brands/suggestions'
    }
    const params = {
      responseType: 'qc-tool',
      fullPath: searchValue,
      isLeaf: true,
      isActive: true,
      condition: action.data.condition,
      limit: LIMIT_EXTENDED_SEARCH_RESULTS
    }
    const result = yield call(() => axios.get(filterApiMap[action.data.type], { params }))
    yield put({
      type: NIQ_FETCH_TYPE_CHILDREN_SUCCEEDED,
      data: result.data.suggestions,
      ruleId: action.data.id,
      ruleGroupLabel: searchValue
    })

    const totalItems = result.data.total

    if (totalItems > LIMIT_EXTENDED_SEARCH_RESULTS) {
      yield put(
        createAlert(
          'warning',
          `Please note: Your selection contains ${totalItems} items, but only ${LIMIT_EXTENDED_SEARCH_RESULTS} have been added to the query.`,
          'Note: Not All Items Were Added to the Query'
        )
      )
    }
  } catch (error) {
    window.captureException(error)
    yield put({
      type: NIQ_FETCH_TYPE_CHILDREN_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'danger',
          message: `Failed to fetch children of ${action.data.value}`,
          headline: 'Search'
        }
      ],
      alertTimeout: 10000
    })
  }
}

function* updateWidgetData(action) {
  const search = yield select(getSearch)
  if (
    search.isActiveTab !== EDIT_MODE ||
    search.isLocked ||
    (action.aggType in search.widgetEnabledStatus && !search.widgetEnabledStatus[action.aggType])
  ) {
    return
  }
  yield queryWithTempFilter([action.aggType])
}

function* fetchGroups() {
  try {
    const response = yield call(() => axios.get('/api/niq-search-query/group'))
    yield put({
      type: NIQ_FETCH_SAVED_GROUP_SUCCEEDED,
      data: response.data || []
    })
  } catch (error) {
    yield put({
      type: NIQ_FETCH_SAVED_GROUP_FAILED
    })
  }
}

function* onCreateGroup(action) {
  try {
    const response = yield call(() => axios.post('/api/niq-search-query/group', { name: action.value }))
    yield put({
      type: NIQ_CREATE_GROUP_SUCCEEDED,
      data: response.data
    })
    yield fetchGroups()
    yield fetchQueriesByGroup(false)
  } catch (error) {
    yield put({
      type: NIQ_CREATE_GROUP_FAILED
    })
  }
}

function* fetchSavedQueries() {
  yield fetchQueriesByGroup(false)
}

function* fetchQueriesByGroup(clear) {
  const search = yield select(getSearch)
  try {
    const response = yield call(() =>
      axios.get('/api/niq-search-query/query', { params: { id: search.queryHeader.group.selectedID } })
    )
    yield put({
      type: NIQ_FETCH_QUERIES_BY_GROUP_SUCCEEDED,
      clear,
      data: response.data || []
    })
  } catch (error) {
    yield put({
      type: NIQ_FETCH_QUERIES_BY_GROUP_FAILED
    })
    yield put(createAlert('danger', error.message, `Queries by Group fetch failed`))
  }
}

function* onCreateGroupQuery(action) {
  const search = yield select(getSearch)
  try {
    const response = yield call(() =>
      axios.post('/api/niq-search-query/query', {
        queryName: action.value,
        query: search.queryTree,
        groupName: search.queryHeader.group.selectedValue
      })
    )
    yield put({
      type: NIQ_CREATE_QUERY_SUCCEEDED,
      data: response.data,
      alerts: [
        {
          id: cuid(),
          type: 'success',
          message: 'Successfully created Query',
          headline: 'Query Created'
        }
      ]
    })
    yield fetchQueriesByGroup(true)
  } catch (error) {
    window.captureException(error)
    const errorMessage = 'Failed to create query group'
    yield put({
      type: NIQ_CREATE_QUERY_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'danger',
          message: errorMessage,
          headline: 'Create Query Group'
        }
      ],
      alertTimeout: 10000
    })
  }
}

function* deleteGroup() {
  const search = yield select(getSearch)
  try {
    const response = yield call(() =>
      axios.delete('/api/niq-search-query/group', { data: { id: search.queryHeader.group.selectedID } })
    )
    yield put({
      type: NIQ_DELETE_GROUP_SUCCEEDED,
      alerts: [
        {
          id: cuid(),
          type: 'success',
          message: response.data,
          headline: 'Group Deleted'
        }
      ]
    })
    yield fetchGroups()
  } catch (error) {
    window.captureException(error)
    const errorMessage = 'Failed to delete query group'
    yield put({
      type: NIQ_DELETE_GROUP_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'danger',
          message: errorMessage,
          headline: 'Delete Query Group'
        }
      ],
      alertTimeout: 10000
    })
  }
}

function* deleteQuery() {
  const search = yield select(getSearch)
  try {
    const response = yield call(() =>
      axios.delete('/api/niq-search-query/query', { data: { id: search.queryHeader.query.selectedID } })
    )
    yield put({
      type: NIQ_DELETE_QUERY_SUCCEEDED,
      alerts: [
        {
          id: cuid(),
          type: 'success',
          message: response.data,
          headline: 'Query Deleted'
        }
      ]
    })
    yield fetchQueriesByGroup(true)
  } catch (error) {
    window.captureException(error)
    const errorMessage = 'Failed to delete query'
    yield put({
      type: NIQ_DELETE_QUERY_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'danger',
          message: errorMessage,
          headline: 'Delete Query'
        }
      ],
      alertTimeout: 10000
    })
  }
}

function* saveSearchQuery() {
  try {
    const search = yield select(getSearch)
    const payload = {
      group: { id: search.queryHeader.group.selectedID, name: search.queryHeader.group.updatedValue },
      query: { id: search.queryHeader.query.selectedID, name: search.queryHeader.query.updatedValue },
      queryTree: search.queryTree
    }
    const response = yield call(() => axios.post('/api/niq-search-query', payload))
    yield put({
      type: NIQ_SAVE_SEARCH_QUERY_SUCCEEDED,
      data: response.data,
      alerts: [
        {
          id: cuid(),
          type: 'success',
          message: `Updated Query Successfully`,
          headline: 'Query Manager'
        }
      ]
    })
  } catch (error) {
    window.captureException(error)
    const errorMessage = 'Failed to update query'
    yield put({
      type: NIQ_SAVE_SEARCH_QUERY_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'danger',
          message: errorMessage,
          headline: 'Query Manager'
        }
      ],
      alertTimeout: 10000
    })
  }
}

function* fetchQuerySQL(action) {
  try {
    yield put({
      type: NIQ_GET_QUERY_SQL_STARTED
    })
    const result = yield call(() =>
      axios.post('/api/niq-search-query/get-sql', { queryTree: action.queryTree }, { timeout: config.extendedTimeout })
    )
    yield put({
      type: NIQ_GET_QUERY_SQL_SUCCEEDED,
      data: result.data.sql
    })
  } catch (error) {
    window.captureException(error)
    yield put({
      type: NIQ_GET_QUERY_SQL_FAILED
    })
    yield put(createAlert('danger', error.message, `Error in fetching sql`))
  }
}

function* overTimeWidgetQuery(action) {
  const search = yield select(getSearch)
  if (
    search.isActiveTab === TRENDS &&
    (search.isStale[TRENDS] || [NIQ_TIME_DIMENSION_CHANGED, NIQ_TIME_AGGREGATION_CHANGED].includes(action.type))
  ) {
    yield query(TIME, null, true)
  }
}

function* updateStatsData() {
  const search = yield select(getSearch)
  if (search.widgetEnabledStatus[STATS]) {
    const queryParams = yield getTempFilterParam()
    yield all([contextDataStats(), queryDataStats({ ...queryParams })])
  }
}

function* sortTable(action) {
  const queryParams = yield getTempFilterParam()
  yield queryAll(action.data.updateAggs, { ...queryParams })
}

function* fetchDescriptionEditSuggestions() {
  const searchEdit = yield select(getSearchEdit)
  const payload = []
  // rows property will always present
  searchEdit.description.edits.rows.forEach(row => {
    if (row.isBrandEditable) {
      payload.push({
        description: row.description,
        merchantId: row.merchantId,
        categoryId: row.category_id,
        descriptionId: row.id
      })
    }
  })

  try {
    const CHUNK_SIZE = 50
    const batches = chunk(payload, CHUNK_SIZE)
    for (const batch of batches) {
      const autoSuggestResult = yield call(() => axios.post('/api/dictionary/edits/suggestions', batch))
      if (autoSuggestResult && autoSuggestResult.data) {
        yield put({
          type: NIQ_FETCH_DESCRIPTION_EDIT_SUGGESTIONS_SUCCEEDED,
          data: autoSuggestResult.data,
          loading: true
        })
      }
    }
    yield put({
      type: NIQ_FETCH_DESCRIPTION_EDIT_SUGGESTIONS_SUCCEEDED,
      loading: false
    })
  } catch (error) {
    window.captureException(error)
    yield put({
      type: NIQ_FETCH_DESCRIPTION_EDIT_SUGGESTIONS_FAILED,
      alerts: [
        {
          id: cuid(),
          type: 'danger',
          message: `Failed to get brand suggestions for some descriptions`,
          headline: 'Description Edit'
        }
      ],
      alertTimeout: 10000,
      data: error.message
    })
  }
}

function* searchSaga() {
  yield takeLatest(action => queryActionWhitelist.includes(action.type), shouldQuery)
  yield takeLatest(NIQ_DESCRIPTION_SAVE_EDITS, saveDictionaryEntries)
  yield takeLatest([NIQ_DESCRIPTION_BRAND_SUGGEST, NIQ_DESCRIPTION_CATEGORY_SUGGEST], autosuggestEdits)
  yield takeLatest(NIQ_SEARCH_TERM_SUGGEST, autosuggest)
  yield takeLatest(NIQ_FETCH_TYPE_CHILDREN, fetchTypeChildren)
  yield takeLatest(NIQ_SEARCH_REFRESH_DESCRIPTION_DATA, queryDescriptions)
  yield takeLatest(NIQ_GET_SEARCH_METADATA, getSearchMetaData)
  yield takeLatest(NIQ_REFRESH_DESCRIPTION_EDIT_STATUS, fetchAllDescriptionEditStatus)
  yield takeLatest(NIQ_APPLY_TEMP_FILTER, applyTempFilter)
  yield takeLatest(NIQ_REMOVE_TEMP_FILTER, removeTempFilter)
  yield takeLatest(NIQ_UPDATE_WIDGET_DATA, updateWidgetData)
  yield takeLatest(NIQ_SORT_TABLE, sortTable)
  yield takeLatest([NIQ_FETCH_SAVED_GROUP, NIQ_RESET_QUERY_HEADER], fetchGroups)
  yield takeLatest(NIQ_GROUP_CREATE, onCreateGroup)
  yield takeLatest(NIQ_FETCH_QUERIES_BY_GROUP, fetchSavedQueries)
  yield takeLatest(NIQ_QUERY_CREATE, onCreateGroupQuery)
  yield takeLatest(NIQ_DELETE_GROUP, deleteGroup)
  yield takeLatest(NIQ_DELETE_QUERY, deleteQuery)
  yield takeLatest(NIQ_SAVE_SEARCH_QUERY, saveSearchQuery)
  yield takeLatest(NIQ_UPDATE_STATS_DATA, updateStatsData)
  yield takeLatest(action => overTimeWidgetActionList.includes(action.type), overTimeWidgetQuery)
  yield takeLatest(NIQ_FETCH_DESCRIPTION_EDIT_SUGGESTIONS, fetchDescriptionEditSuggestions)
  yield takeLatest(NIQ_GET_QUERY_SQL, fetchQuerySQL)
}

export default searchSaga
